import {
    doScheduleAllocationsSumTo100,
    MAX_TERM_VALUE_BY_UNIT,
} from "@common/constants/domains/accounting/schedules";
import { hasMilestoneComponent } from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import { PreviewScheduleRecognitionBody } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import {
    hasTermRecognitionComponent,
    ScheduleTermUnit,
} from "@common/types/domains/accounting/schedule";
import { ScheduleTemplateFormValues } from "./scheduleFormHelpers";
import { toMilestoneBodies } from "./scheduleMilestoneFormHelpers";
import {
    buildMilestonePreviewUnavailableMessage,
    hasValidExampleAmount,
    toMilestoneExampleDatesBody,
} from "./scheduleMilestonePreviewHelpers";
import {
    resolvePreviewAnchorUsage,
    resolvePreviewTermDates,
} from "./schedulePreviewHelpers";

export interface PreviewRequest {
    body: PreviewScheduleRecognitionBody;
    labelDates: {
        sourceDate: string | undefined;
        specificDate: string | undefined;
    };
}

/** Either the request to send, or why the form cannot preview yet. */
export type PreviewRequestResult =
    | { request: PreviewRequest; unavailableMessage: null }
    | { request: null; unavailableMessage: string };

export const TERM_PREVIEW_UNAVAILABLE_MESSAGE =
    "Preview appears when component allocations total 100% and the term is valid.";

function unavailable(unavailableMessage: string): PreviewRequestResult {
    return { request: null, unavailableMessage };
}

function toComponentBodies(
    values: ScheduleTemplateFormValues,
): PreviewScheduleRecognitionBody["components"] {
    return values.components.map((component) => ({
        method: component.method,
        anchor: component.anchor,
        offsetDays: component.offsetDays,
        allocationPercent: component.allocationPercent,
    }));
}

/** A milestone policy previews from its example source day; each row may carry its own day. */
export function buildMilestonePreviewRequest(
    values: ScheduleTemplateFormValues,
): PreviewRequestResult {
    const unavailableMessage = buildMilestonePreviewUnavailableMessage(values);
    if (unavailableMessage !== null) {
        return unavailable(unavailableMessage);
    }
    const request: PreviewRequest = {
        body: {
            amount: values.exampleAmount,
            termStart: values.milestoneExampleSourceDate,
            termLength: 1,
            termUnit: ScheduleTermUnit.months,
            components: toComponentBodies(values),
            milestones: toMilestoneBodies(values),
            milestoneExampleDates: toMilestoneExampleDatesBody({
                milestones: values.milestones,
                exampleDates: values.milestoneExampleDates,
            }),
        },
        labelDates: {
            sourceDate: values.milestoneExampleSourceDate,
            specificDate: undefined,
        },
    };
    return { request, unavailableMessage: null };
}

export function buildPreviewRequest(
    values: ScheduleTemplateFormValues,
): PreviewRequestResult {
    if (hasMilestoneComponent(values.components)) {
        return buildMilestonePreviewRequest(values);
    }
    const allocationPercents = values.components.map(
        (component) => component.allocationPercent,
    );
    if (!doScheduleAllocationsSumTo100(allocationPercents)) {
        return unavailable(TERM_PREVIEW_UNAVAILABLE_MESSAGE);
    }
    if (
        values.components.some((component) => component.allocationPercent <= 0)
    ) {
        return unavailable(TERM_PREVIEW_UNAVAILABLE_MESSAGE);
    }
    if (!hasValidExampleAmount(values.exampleAmount)) {
        return unavailable(TERM_PREVIEW_UNAVAILABLE_MESSAGE);
    }
    const hasTermMethod = hasTermRecognitionComponent(values.components);
    const isTermLengthValid =
        Number.isInteger(values.termLength) &&
        values.termLength >= 1 &&
        values.termLength <= MAX_TERM_VALUE_BY_UNIT[values.termUnit];
    if (hasTermMethod && !isTermLengthValid) {
        return unavailable(TERM_PREVIEW_UNAVAILABLE_MESSAGE);
    }

    const previewDates = resolvePreviewTermDates({
        sourceDate: values.exampleSourceDate,
        specificDate: values.exampleSpecificDate,
        components: values.components,
    });

    const anchorUsage = resolvePreviewAnchorUsage(values.components);

    const request: PreviewRequest = {
        body: {
            amount: values.exampleAmount,
            termStart: previewDates.termStart,
            specificDate: previewDates.specificDate,
            termLength: hasTermMethod ? values.termLength : 1,
            termUnit: values.termUnit,
            components: toComponentBodies(values),
            milestones: [],
        },
        labelDates: {
            sourceDate: anchorUsage.usesSourceDate
                ? values.exampleSourceDate
                : undefined,
            specificDate: anchorUsage.usesSpecificDate
                ? values.exampleSpecificDate
                : undefined,
        },
    };
    return { request, unavailableMessage: null };
}
